import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchDriver,
  FetchPersonaInquiry,
  SetPersonaClientCompletedInquiry,
  SetPersonaSessionToken,
} from 'src/constants/actionTypes'
import { ApiService, callClientSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import { PersonaInquiry } from 'src/interfaces/persona'
import { PersonaInquiryType } from 'src/constants/persona'
import { captureSentryError } from 'src/utils/sentry'

interface FetchPersonaInquiryRequest {
  stepId: string
  inquiryType?: PersonaInquiryType
}

function* onFetchPersonaInquiry(action: AsyncAction<FetchPersonaInquiryRequest, PersonaInquiry>) {
  try {
    const { payload }: { payload: PersonaInquiry } = yield call(
      callClientSecureApi,
      Endpoints.FetchPersonaInquiry,
      {
        apiService: ApiService.Paraworks,
        query: {
          stepId: action.payload.stepId,
          inquiryType: action.payload.inquiryType,
        },
      },
    )

    yield put(FetchPersonaInquiry.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.persona.onFetchPersonaInquiry] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to fetch inquiry'

    yield put(FetchPersonaInquiry.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SetPersonaSessionTokenRequest {
  sessionToken: string
  stepId: string
}

function* onSetPersonaSessionToken(action: AsyncAction<SetPersonaSessionTokenRequest>) {
  try {
    const { sessionToken, stepId } = action.payload

    yield call(callClientSecureApi, Endpoints.SetPersonaSessionToken, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      body: {
        sessionToken,
      },
      query: {
        stepId,
      },
    })

    yield put(SetPersonaSessionToken.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.persona.onSetPersonaSessionToken] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to set session token'

    yield put(SetPersonaSessionToken.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface SetPersonaClientCompletedInquiryRequest {
  stepId: string
}

function* onSetPersonaClientCompletedInquiry(
  action: AsyncAction<SetPersonaClientCompletedInquiryRequest>,
) {
  try {
    yield call(callClientSecureApi, Endpoints.SetPersonaClientCompletedInquiry, {
      apiService: ApiService.Paraworks,
      method: 'POST',
      query: {
        stepId: action.payload.stepId,
      },
    })

    yield put(SetPersonaClientCompletedInquiry.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.persona.onSetPersonaClientCompletedInquiry] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to set client completed inquiry'

    yield put(SetPersonaClientCompletedInquiry.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* personaSaga() {
  yield takeEvery(FetchPersonaInquiry.type.REQUEST, onFetchPersonaInquiry)
  yield takeEvery(SetPersonaSessionToken.type.REQUEST, onSetPersonaSessionToken)
  yield takeEvery(SetPersonaClientCompletedInquiry.type.REQUEST, onSetPersonaClientCompletedInquiry)
}
